import { Button } from "design-system/atoms/Button"
import { cn } from "design-system/utils"
import { ListFilter, Plus, SearchIcon } from "lucide-react"
import { useGetBackofficeCustomersQuery } from "modules/backoffice/hooks/customers/useGetBackofficeCustomersQuery"
import { Pagination } from "modules/shared/components/Pagination"
import { SearchQuery } from "modules/shared/components/SearchQuery"
import { usePagination } from "modules/shared/hooks/usePagination"
import { useSearchQuery } from "modules/shared/hooks/useSearchQuery"
import { useSortBy } from "modules/shared/hooks/useSortBy"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Route as CustomersListRoute } from "routes/_base/_auth/backoffice/organizations/$id/companies/$companyId/customers/list"
import { customersSortFields } from "schema"
import { BackofficeCustomerCreator } from "./BackofficeCustomerCreator"
import { BackofficeCustomersTable } from "./BackofficeCustomersTable"

const ITEMS_PER_PAGE = 25

export type CustomersSortFields = (typeof customersSortFields)[number]

export const BackofficeCustomers = () => {
  const { t } = useTranslation()
  const search = CustomersListRoute.useSearch()
  const [filtersVisible, setFiltersVisible] = useState(false)
  const [isCreateCustomerOpen, setIsCreateCustomerOpen] = useState(false)
  const { companyId } = CustomersListRoute.useParams()

  const { page, totalPages, setPage, setCount, count } = usePagination({
    search,
    fullPath: CustomersListRoute.fullPath,
    total: ITEMS_PER_PAGE,
  })

  const { query, setQuery } = useSearchQuery({
    search,
    fullPath: CustomersListRoute.fullPath,
    onSearchQueryChange: () => setPage(1),
  })

  const { onSort, sortBy, fields } = useSortBy<CustomersSortFields[]>({
    search,
    fullPath: CustomersListRoute.fullPath,
    fields: [...customersSortFields],
  })

  const { data: customers, isPending } = useGetBackofficeCustomersQuery({
    query: {
      companyId,
      query,
      page,
      total: ITEMS_PER_PAGE,
      sortBy: sortBy ?? { field: "id", order: "asc" },
      filterBy: {
        name: search.filterBy?.name,
      },
    },
    onSuccess: (data) => {
      setCount(data.pageInfo.count)
    },
  })

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-2 justify-end">
        <div
          className={cn(
            "transition-all duration-200 w-full",
            filtersVisible
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 max-h-0 max-w-0 pointer-events-none overflow-hidden",
          )}
        >
          <div className="flex flex-col sm:flex-row gap-2">
            <SearchQuery
              placeholder={t("customers.table.search")}
              setQuery={setQuery}
              defaultValue={query}
              icon={SearchIcon}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <div
            onClick={() => setFiltersVisible((prev) => !prev)}
            className={cn(
              "h-10 w-10 flex items-center justify-center cursor-pointer shrink-0",
              filtersVisible ? "bg-primary/5 text-primary rounded-md" : "text-muted-foreground",
            )}
          >
            <ListFilter size={20} />
          </div>
          <div className="h-10">
            <Button
              variant="default"
              className="h-full"
              onClick={() => setIsCreateCustomerOpen(true)}
            >
              <span>
                <Plus className="size-4" strokeWidth={2} />
              </span>
              <span>{t("backoffice.customers.createCustomer")}</span>
            </Button>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <BackofficeCustomersTable
          customers={customers}
          isPending={isPending}
          fields={fields}
          onSort={onSort}
          search={search}
        />
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          count={count}
        />
      </div>
      {isCreateCustomerOpen && (
        <BackofficeCustomerCreator onOpenChange={() => setIsCreateCustomerOpen(false)} />
      )}
    </>
  )
}
